/**
 * What the guide is told while it is leading, and nothing more.
 *
 * The transport already exists: `dispatchTap` puts the action in `lastTap`,
 * `App` mixes it into `context.a2ui_action`, and `userMessage` goes into the
 * thread byte for byte as the visitor's own line (`a2ui/actions.ts`). A
 * navigation event is an ordinary tap on that wire — nothing new travels.
 *
 * Three events, and the NAME is the contract: `nav_arrived`, `nav_off_route`,
 * `nav_stop`. A fourth is deliberately absent — **the frontend never synthesises
 * `nav_reroute_confirm`.** Recomputing a route is irreversible enough to need
 * the visitor's own tap, and that tap is a chip the backend sends, travelling
 * the ordinary `Chips.tsx` → `readAction` → `dispatchTap` path. A second
 * implementation of a confirmation on the client is the class of bug this
 * repository is full of.
 *
 * **The wording of `userMessage` follows two rules this repo already holds.**
 * A museum label is never DECLINED by rule (`tools._transport_ask`: a live
 * string keeps its nominative behind a governing noun), so it is «Я у точки
 * «Монплезир»» and not «Я у Монплезира». And the sentence never assumes the
 * visitor's gender — «Кажется, я не на маршруте», not «я отклонился» — because
 * the guide knows who it is and does not know who they are.
 */

import type { A2uiAction } from '../a2ui/actions';
import type { Fix, StopModel } from './track';

export const NAV_EVENT_PREFIX = 'nav_';

/** Where the visitor was when the event fired. Never streamed, only attached. */
function whereContext(fix: Fix | null): Record<string, unknown> {
  if (!fix) return {};
  return {
    lat: round6(fix.lat),
    lon: round6(fix.lng),
    accuracy_m: Math.round(fix.accuracyM),
  };
}

function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * `index` is the stop's `n`, NOT its position in an array.
 *
 * The whole wire is keyed on `n` — `current` is one, `legs[].n_to` is one — so
 * an array index here would be a second numbering of the same walk, agreeing
 * with the first only while every pin is numbered contiguously from 1. Agreed
 * with the backend as `n`.
 */
export function arrivedAction(stop: StopModel, distanceM: number, fix: Fix | null): A2uiAction {
  return {
    event: {
      name: 'nav_arrived',
      userMessage: `Я у точки «${stop.label}»`,
      context: {
        stop_id: stop.id,
        index: stop.n,
        distance_m: Math.round(distanceM),
        ...whereContext(fix),
      },
    },
  };
}

export function offRouteAction(
  distanceM: number,
  nearest: StopModel | null,
  fix: Fix | null,
): A2uiAction {
  return {
    event: {
      name: 'nav_off_route',
      userMessage: 'Кажется, я не на маршруте',
      context: {
        distance_m: Math.round(distanceM),
        nearest_stop_id: nearest?.id ?? null,
        ...whereContext(fix),
      },
    },
  };
}

export function stopAction(fix: Fix | null): A2uiAction {
  return {
    event: {
      name: 'nav_stop',
      userMessage: 'Хватит вести, вернёмся к доске',
      context: { ...whereContext(fix) },
    },
  };
}

/** Was this the guide's own navigation event? Reads the NAME, never the prose. */
export function isNavEvent(name: unknown): boolean {
  return typeof name === 'string' && name.startsWith(NAV_EVENT_PREFIX);
}
